/**
 * enonceReadingAdapter — la lecture de l'élève, telle que le compte la sert (`profile.reading`) →
 * ce que l'énoncé du design system applique, et le choix de la fiche « Ma lecture » → ce que
 * `PUT user/reading-settings` attend.
 *
 * Portage littéral de l'ancien `EnonceReader` : seul l'élève, sur son propre compte, a une lecture.
 * Le tuteur, le relecteur et le parent (le mode parent passe par le compte de l'élève) gardent la
 * feuille standard, sans « Aa ». La lecture adaptée d'office d'un élève signalé Dys n'est pas
 * décidée ici : le serveur la résout (`StudentProfile::readingSettings()`), l'app l'applique. Les
 * préréglages sont ceux du serveur, partagés avec l'ancien espace (`data/utility/readingPresets`).
 */
import { type UIEnonceReading } from "../../../ui/types"
import { hasRole } from "../../../../../data/utility/ability"
import {
    isCustomReading,
    READING_ADAPTED,
    READING_STANDARD,
    readingChoice,
    sameReading,
} from "../../../../../data/utility/readingPresets"

/** Formes lâches (`ports/CLAUDE.md` § 5). */
export type ApiEnonceReading = {
    adapted?: boolean | null
    font?: string | null
    size?: number | null
    applied_by_default?: boolean | null
}

export type ApiReader = {
    roles?: string[] | null
    profile?: { reading?: ApiEnonceReading | null } | null
}

export type ApiReadingChoice = { adapted: boolean; font: UIEnonceReading["font"]; size: number }

function fromApi(reading: ApiEnonceReading): UIEnonceReading {
    return {
        adapted: reading.adapted === true,
        font: reading.font === "lexend" ? "lexend" : "standard",
        size: typeof reading.size === "number" ? reading.size : READING_STANDARD.size,
    }
}

/**
 * La lecture du compte connecté, s'il lit pour lui-même : un élève, hors mode parent, dont le
 * compte porte une lecture. Sinon `null` (la feuille standard, sans « Aa »), comme un ancien
 * backend qui ne la servait pas.
 */
export function toEnonceReading(user: ApiReader | null | undefined, isParentMode: boolean): UIEnonceReading | null {
    const reading = user?.profile?.reading
    if (!hasRole(user, "student") || isParentMode || reading == null) return null
    return fromApi(reading)
}

/** Ce que l'élève choisit, sous les clés du serveur, sans ce qu'il en déduit (`applied_by_default`). */
export function toReadingChoice(reading: UIEnonceReading): ApiReadingChoice {
    return readingChoice(reading)
}

/** L'interrupteur « Lecture adaptée » : l'un des deux préréglages, d'un coup. */
export function readingPreset(adapted: boolean): UIEnonceReading {
    return fromApi(adapted ? READING_ADAPTED : READING_STANDARD)
}

/** Deux lectures qui s'affichent pareil. */
export function sameEnonceReading(a: UIEnonceReading, b: UIEnonceReading): boolean {
    return sameReading(a, b)
}

/** La lecture n'est plus le standard : « Aa » le montre, sans pour autant crier. */
export function isCustomEnonceReading(reading: UIEnonceReading): boolean {
    return isCustomReading(reading)
}
